import React, { useState } from 'react';
import { Plus, Target } from 'lucide-react';
import { useFinanceStore } from '../store/useFinanceStore';
import GoalCard from '../components/GoalCard';
import NewGoalModal from '../components/NewGoalModal';
import GoalComparisonChart from '../components/charts/GoalComparisonChart';
import Carousel from '../components/Carousel';
import { motion } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';

const Metas = () => {
  const { goals } = useFinanceStore();
  const [isNewGoalModalOpen, setIsNewGoalModalOpen] = useState(false);
  const hasGoals = goals.length > 0;
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const totalSaved = goals.reduce((sum, goal) => sum + goal.currentAmount, 0);

  const headerContainerClass = `rounded-2xl p-6 border transition-colors ${
    isDark
      ? 'bg-slate-900 border-slate-800 shadow-[0_18px_45px_rgba(15,23,42,0.35)]'
      : 'bg-white border-teal-200 shadow-[0_18px_45px_rgba(45,212,191,0.18)]'
  }`;

  const overviewContainerClass = `rounded-2xl p-6 border transition-colors ${
    isDark
      ? 'bg-slate-900 border-slate-800 shadow-[0_18px_45px_rgba(15,23,42,0.35)]'
      : 'bg-gradient-to-br from-white via-teal-50 to-cyan-50 border-teal-200 shadow-[0_20px_45px_rgba(94,234,212,0.25)]'
  }`;

  const primaryButtonClass = `px-7 py-3 rounded-xl flex items-center gap-2 font-semibold transition-all duration-200 ${
    isDark
      ? 'bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white shadow-[0_16px_40px_rgba(16,185,129,0.35)]'
      : 'bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-500 hover:to-emerald-600 text-white shadow-[0_16px_40px_rgba(45,212,191,0.35)]'
  }`;

  const emptyStateContainerClass = `rounded-2xl border transition-colors px-8 py-16 flex flex-col items-center text-center max-w-2xl mx-auto ${
    isDark
      ? 'bg-slate-900 border-slate-800 shadow-[0_18px_45px_rgba(15,23,42,0.35)]'
      : 'bg-white border-teal-200 shadow-[0_20px_45px_rgba(94,234,212,0.25)]'
  }`;
  const addCardClass = `rounded-2xl border-2 border-dashed flex items-center justify-center cursor-pointer transition-all duration-300 ${
    isDark
      ? 'bg-slate-900/80 border-slate-700 hover:border-emerald-400'
      : 'bg-white border-teal-200 hover:border-emerald-400 shadow-[0_12px_30px_rgba(45,212,191,0.15)]'
  }`;
  const totalTarget = goals.reduce((sum, goal) => sum + goal.targetAmount, 0);
  const overallProgress = totalTarget > 0 ? (totalSaved / totalTarget) * 100 : 0;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="space-y-6"
      >
        <div className={headerContainerClass}>
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-3">
              <span
                className={`text-sm font-semibold uppercase tracking-wide ${
                  isDark ? 'text-emerald-300' : 'text-teal-500'
                }`}
              >
                Plano de metas
              </span>
              <h1 className={`text-2xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Acompanhe suas metas financeiras
              </h1>
              <p className={`${isDark ? 'text-slate-300' : 'text-slate-600'} max-w-2xl`}>
                Visualize objetivos, acompanhe o progresso consolidado e mantenha suas conquistas alinhadas ao planejamento.
              </p>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsNewGoalModalOpen(true)}
              className={primaryButtonClass}
            >
              <Plus size={18} />
              Nova Meta
            </motion.button>
          </div>
        </div>

        {hasGoals ? (
          <>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className={overviewContainerClass}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className={`p-3 rounded-full ${isDark ? 'bg-violet-500/20' : 'bg-violet-100'}`}>
                  <Target className={isDark ? 'text-violet-400' : 'text-violet-600'} size={24} />
                </div>
                <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Resumo das Metas
                </h3>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                <div className="text-center">
                  <p className={`text-2xl sm:text-3xl font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {goals.length}
                  </p>
                  <p className={`text-sm sm:text-base ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                    Metas ativas
                  </p>
                </div>
                <div className="text-center">
                  <p
                    className={`text-2xl sm:text-3xl font-bold mb-1 ${
                      isDark ? 'text-emerald-400' : 'text-emerald-600'
                    }`}
                  >
                    R$ {totalSaved.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                  <p className={`text-sm sm:text-base ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                    Total poupado
                  </p>
                </div>
                <div className="text-center">
                  <p
                    className={`text-2xl sm:text-3xl font-bold mb-1 ${
                      isDark ? 'text-violet-400' : 'text-violet-600'
                    }`}
                  >
                    R$ {totalTarget.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                  <p className={`text-sm sm:text-base ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                    Meta total
                  </p>
                </div>
                <div className="text-center">
                  <p
                    className={`text-2xl sm:text-3xl font-bold mb-1 ${
                      isDark ? 'text-cyan-400' : 'text-cyan-600'
                    }`}
                  >
                    {overallProgress.toFixed(1)}%
                  </p>
                  <p className={`text-sm sm:text-base ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                    Progresso geral
                  </p>
                </div>
              </div>

              {totalTarget > 0 && (
                <div className="mt-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                      Progresso consolidado
                    </span>
                    <span className={`text-sm font-semibold ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`}>
                      {overallProgress.toFixed(1)}%
                    </span>
                  </div>
                  <div className={`w-full h-3 rounded-full ${isDark ? 'bg-slate-800' : 'bg-teal-100'}`}>
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min(overallProgress, 100)}%` }}
                      transition={{ duration: 1, ease: 'easeOut' }}
                      className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-violet-500"
                    />
                  </div>
                </div>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              <Carousel itemClassName="w-full" gap={18}>
                <GoalComparisonChart />
                {goals.map((goal, index) => (
                  <div key={goal.id}>
                    <GoalCard goal={goal} />
                  </div>
                ))}
                <div onClick={() => setIsNewGoalModalOpen(true)} className={addCardClass} style={{ cursor: 'pointer' }}>
                  <div className="text-center">
                    <div className={`p-4 rounded-full mx-auto mb-4 ${isDark ? 'bg-emerald-500/10' : 'bg-emerald-100'}`}>
                      <Plus className={isDark ? 'text-emerald-300' : 'text-emerald-600'} size={32} />
                    </div>
                    <p className={`font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                      Adicionar nova meta
                    </p>
                  </div>
                </div>
              </Carousel>
            </motion.div>
          </>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className={emptyStateContainerClass}
          >
            <div className={`p-6 rounded-full mb-6 ${isDark ? 'bg-emerald-500/10' : 'bg-teal-100'}`}>
              <Target className={isDark ? 'text-emerald-300' : 'text-teal-600'} size={56} />
            </div>
            <h3 className={`text-2xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Nenhuma meta cadastrada
            </h3>
            <p className={`${isDark ? 'text-slate-300' : 'text-slate-600'} mb-6`}>
              Comece criando sua primeira meta para visualizar o progresso e celebrar cada conquista.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsNewGoalModalOpen(true)}
              className={`${primaryButtonClass} justify-center`}
            >
              <Target size={18} />
              Criar primeira meta
            </motion.button>
          </motion.div>
        )}
      </motion.div>

      <NewGoalModal
        isOpen={isNewGoalModalOpen}
        onClose={() => setIsNewGoalModalOpen(false)}
      />
    </>
  );
};

export default Metas;